import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

/**
 * Floating action buttons that follow the user on every page:
 *  - Primary: "Book Appointment" -> /appointment
 *  - Secondary: "Ask our AI Assistant" -> opens a small chat panel (placeholder)
 *  - Tertiary: WhatsApp / Phone for quick contact
 *
 * Hidden on appointment & patient-login pages where it would be redundant.
 */
const HIDDEN_PATHS = ["/appointment", "/patient-login", "/patient-profile"];

const FloatingActions = () => {
  const { pathname } = useLocation();
  const [aiOpen, setAiOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 240);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (HIDDEN_PATHS.some((p) => pathname.startsWith(p))) return null;

  return (
    <>
      <div
        className="mh-fab-wrap"
        style={{
          position: "fixed",
          right: "20px",
          bottom: "24px",
          zIndex: 9990,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          alignItems: "flex-end",
          pointerEvents: "none",
        }}
      >
        {/* AI panel */}
        {aiOpen && (
          <div
            style={{
              pointerEvents: "auto",
              width: "320px",
              maxWidth: "calc(100vw - 40px)",
              background: "#fff",
              borderRadius: "16px",
              boxShadow: "0 20px 50px rgba(7,108,236,0.25)",
              border: "1px solid rgba(7,108,236,0.08)",
              overflow: "hidden",
              animation: "mhFabFade .25s ease",
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg,#076cec 0%,#04b3bf 100%)",
                color: "#fff",
                padding: "14px 18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.2)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i className="fa-solid fa-robot"></i>
                </span>
                <div style={{ lineHeight: 1.15 }}>
                  <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>Maroof AI Assistant</div>
                  <div style={{ fontSize: "0.7rem", opacity: 0.85 }}>How can we help you today?</div>
                </div>
              </div>
              <button
                onClick={() => setAiOpen(false)}
                aria-label="Close"
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#fff",
                  fontSize: "1.1rem",
                  cursor: "pointer",
                  padding: 4,
                }}
              >
                <i className="fa-regular fa-xmark"></i>
              </button>
            </div>
            <div style={{ padding: "16px 18px", color: "#1a2744", fontSize: "0.875rem", lineHeight: 1.55 }}>
              <p style={{ margin: "0 0 12px" }}>
                Hi! I can help you book an appointment, find a doctor, or answer questions about our services.
              </p>
              <div style={{ display: "grid", gap: "8px" }}>
                <Link
                  to="/appointment"
                  onClick={() => setAiOpen(false)}
                  style={{
                    background: "#f1f5f9",
                    padding: "10px 12px",
                    borderRadius: "10px",
                    color: "#076cec",
                    fontWeight: 600,
                    textDecoration: "none",
                    fontSize: "0.85rem",
                  }}
                >
                  <i className="fa-regular fa-calendar-check" style={{ marginRight: 8 }}></i>
                  Book an appointment
                </Link>
                <Link
                  to="/team"
                  onClick={() => setAiOpen(false)}
                  style={{
                    background: "#f1f5f9",
                    padding: "10px 12px",
                    borderRadius: "10px",
                    color: "#076cec",
                    fontWeight: 600,
                    textDecoration: "none",
                    fontSize: "0.85rem",
                  }}
                >
                  <i className="fa-regular fa-user-doctor" style={{ marginRight: 8 }}></i>
                  Find a doctor
                </Link>
                <Link
                  to="/departments"
                  onClick={() => setAiOpen(false)}
                  style={{
                    background: "#f1f5f9",
                    padding: "10px 12px",
                    borderRadius: "10px",
                    color: "#076cec",
                    fontWeight: 600,
                    textDecoration: "none",
                    fontSize: "0.85rem",
                  }}
                >
                  <i className="fa-regular fa-hospital" style={{ marginRight: 8 }}></i>
                  Browse departments
                </Link>
                <a
                  href="https://wa.me/925111644911"
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    background: "#dcfce7",
                    padding: "10px 12px",
                    borderRadius: "10px",
                    color: "#15803d",
                    fontWeight: 600,
                    textDecoration: "none",
                    fontSize: "0.85rem",
                  }}
                >
                  <i className="fa-brands fa-whatsapp" style={{ marginRight: 8 }}></i>
                  Chat on WhatsApp
                </a>
                <a
                  href="tel:+92-51-111-644-911"
                  style={{
                    background: "#fee2e2",
                    padding: "10px 12px",
                    borderRadius: "10px",
                    color: "#b91c1c",
                    fontWeight: 600,
                    textDecoration: "none",
                    fontSize: "0.85rem",
                  }}
                >
                  <i className="fa-solid fa-phone-volume" style={{ marginRight: 8 }}></i>
                  24/7 Helpline: 111-644-911
                </a>
              </div>
            </div>
          </div>
        )}

        {/* AI button */}
        <button
          onClick={() => setAiOpen((o) => !o)}
          aria-label="Open AI assistant"
          style={{
            pointerEvents: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            background: "linear-gradient(135deg,#04b3bf 0%,#076cec 100%)",
            color: "#fff",
            border: "none",
            padding: "12px 18px",
            borderRadius: "999px",
            fontWeight: 700,
            fontSize: "0.85rem",
            boxShadow: "0 10px 28px rgba(4,179,191,0.45)",
            cursor: "pointer",
          }}
        >
          <i className="fa-solid fa-robot"></i>
          {!aiOpen && "Ask our AI"}
        </button>

        {/* Sticky Book Appointment */}
        {/* <Link
          to="/appointment"
          aria-label="Book Appointment"
          style={{
            pointerEvents: "auto",
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            background: "linear-gradient(135deg,#dc2626 0%,#b91c1c 100%)",
            color: "#fff",
            padding: "14px 22px",
            borderRadius: "999px",
            fontWeight: 800,
            fontSize: "0.92rem",
            textDecoration: "none",
            boxShadow: "0 12px 30px rgba(220,38,38,0.5)",
            transform: visible ? "translateY(0)" : "translateY(0)",
          }}
        >
          <i className="fa-regular fa-calendar-plus"></i>
          Book Appointment
        </Link> */}
      </div>
      <style>{`@keyframes mhFabFade{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:translateY(0);}}`}</style>
    </>
  );
};

export default FloatingActions;
